import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronDown, CheckCircle2, Circle, AlertTriangle,
  RefreshCw, Sparkles, ArrowRight,
} from "lucide-react";
import { MARKET_PROFESSIONS } from "@/components/market/marketData";
import { PROFESSION_ICONS } from "@/components/market/ProfessionCard";
import { db } from "@/api/db";

const SORTED_PROFESSIONS = [...MARKET_PROFESSIONS].sort((a, b) =>
  a.name.localeCompare(b.name, "pt-BR")
);

const LOADING_STEPS = [
  { label: "Carregando dados das profissões", detail: "Preparando informações para análise" },
  { label: "Comparando rotinas e entregas", detail: "Analisando dia a dia e resultados esperados" },
  { label: "Analisando remuneração e barreira", detail: "Cruzando dados de mercado e acesso" },
  { label: "Gerando insights personalizados", detail: "A IA está criando a comparação" },
  { label: "Finalizando comparação", detail: "Compilando todos os resultados" },
];

function ComparisonLoading() {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(s => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-lg mx-auto py-12 space-y-8">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "#EAFAF6" }}>
          <motion.div
            className="w-7 h-7 rounded-full border-[3px]"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
        <h3 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Comparando carreiras…
        </h3>
        <p className="text-sm" style={{ color: "#646C6F" }}>
          A IA está analisando as duas profissões. Leva alguns segundos.
        </p>
      </div>

      <div className="space-y-2">
        {LOADING_STEPS.map((step, i) => {
          const done = i < currentStep;
          const active = i === currentStep;
          const pending = i > currentStep;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: pending ? 0.35 : 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="flex items-start gap-3 px-4 py-3 rounded-2xl"
              style={{
                background: done ? "#EAFAF6" : active ? "#F9FAFA" : "transparent",
                border: active ? "1.5px solid #00DCA4" : "1.5px solid transparent",
              }}
            >
              <div className="mt-0.5 flex-shrink-0">
                {done && <CheckCircle2 className="w-4 h-4" style={{ color: "#00DCA4" }} />}
                {active && (
                  <motion.div
                    className="w-4 h-4 rounded-full border-2"
                    style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                  />
                )}
                {pending && <Circle className="w-4 h-4" style={{ color: "#C8CED0" }} />}
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight" style={{ color: done ? "#00664C" : active ? "#003326" : "#99A6AB" }}>
                  {step.label}
                </p>
                {(done || active) && (
                  <p className="text-xs mt-0.5" style={{ color: done ? "#00664C" : "#646C6F" }}>
                    {step.detail}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function ProfessionDropdown({ value, onChange, disabledId, label }) {
  return (
    <div className="flex-1 min-w-0">
      <label className="text-xs font-semibold mb-1.5 block" style={{ color: "#646C6F" }}>{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full text-sm font-semibold px-4 py-3 rounded-xl appearance-none focus:outline-none cursor-pointer"
          style={{ background: "#F9FAFA", color: value ? "#003326" : "#99A6AB", border: "1.5px solid #E5E9EA" }}
        >
          <option value="">Selecionar profissão</option>
          {SORTED_PROFESSIONS.map(p => (
            <option key={p.id} value={p.id} disabled={p.id === disabledId}>
              {p.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: "#99A6AB" }} />
      </div>
    </div>
  );
}

function ComparisonResult({ result, profA, profB, onNewComparison, onBack }) {
  const IconA = PROFESSION_ICONS[profA.id];
  const IconB = PROFESSION_ICONS[profB.id];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="rounded-2xl p-5 text-center" style={{ background: "linear-gradient(135deg, #003326, #00664C)" }}>
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,220,164,0.2)" }}>
              {IconA && <IconA className="w-4.5 h-4.5" style={{ color: "#61E8C6" }} />}
            </div>
            <span className="text-sm font-bold" style={{ color: "#fff" }}>{profA.name}</span>
          </div>
          <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.15)", color: "#B0FCEA" }}>vs</span>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,220,164,0.2)" }}>
              {IconB && <IconB className="w-4.5 h-4.5" style={{ color: "#61E8C6" }} />}
            </div>
            <span className="text-sm font-bold" style={{ color: "#fff" }}>{profB.name}</span>
          </div>
        </div>
        <p className="text-sm" style={{ color: "#B0FCEA" }}>{result.summary}</p>
      </div>

      {/* Dimensions */}
      {(result.dimensions || []).map((dim, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
          className="rounded-2xl bg-white p-4"
          style={{ border: "1px solid #E5E9EA" }}
        >
          <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#99A6AB" }}>{dim.label}</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="rounded-xl p-3" style={{ background: "#EAFAF6" }}>
              <p className="text-xs font-semibold mb-1" style={{ color: "#00664C" }}>{profA.name}</p>
              <p className="text-sm" style={{ color: "#003326" }}>{dim.a_highlight}</p>
            </div>
            <div className="rounded-xl p-3" style={{ background: "#F2F4F4" }}>
              <p className="text-xs font-semibold mb-1" style={{ color: "#646C6F" }}>{profB.name}</p>
              <p className="text-sm" style={{ color: "#2E3233" }}>{dim.b_highlight}</p>
            </div>
          </div>
          <p className="text-xs" style={{ color: "#646C6F" }}>
            <Sparkles className="w-3 h-3 inline-block mr-1" style={{ color: "#00DCA4" }} />
            {dim.insight}
          </p>
        </motion.div>
      ))}

      {/* Synergies */}
      {result.synergies?.length > 0 && (
        <div className="rounded-2xl bg-white p-4" style={{ border: "1px solid #E5E9EA" }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#99A6AB" }}>Habilidades Transferíveis</p>
          <div className="flex flex-wrap gap-2">
            {result.synergies.map((s, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: "#EAFAF6", color: "#00664C" }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Key Differences */}
      {result.key_differences?.length > 0 && (
        <div className="rounded-2xl bg-white p-4" style={{ border: "1px solid #E5E9EA" }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#99A6AB" }}>Diferenças-Chave</p>
          <div className="space-y-2">
            {result.key_differences.map((d, i) => (
              <div key={i} className="flex gap-2 items-start">
                <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#00DCA4" }} />
                <p className="text-sm" style={{ color: "#2E3233" }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendation */}
      {result.recommendation && (
        <div className="rounded-2xl p-4" style={{ background: "#EAFAF6", border: "1px solid #B0FCEA" }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#00664C" }}>Recomendação</p>
          <p className="text-sm leading-relaxed" style={{ color: "#003326" }}>{result.recommendation}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pb-8">
        <button
          onClick={onNewComparison}
          className="flex-1 py-3.5 rounded-full text-sm font-bold flex items-center justify-center gap-2"
          style={{ background: "#00DCA4", color: "#003326" }}
        >
          <RefreshCw className="w-4 h-4" /> Nova comparação
        </button>
        <button
          onClick={onBack}
          className="flex-1 py-3.5 rounded-full text-sm font-bold flex items-center justify-center"
          style={{ background: "#F2F4F4", color: "#646C6F" }}
        >
          Voltar ao mapa
        </button>
      </div>
    </motion.div>
  );
}

export default function CareerComparison({ onBack }) {
  const [phase, setPhase] = useState("select"); // select | loading | result
  const [idA, setIdA] = useState("");
  const [idB, setIdB] = useState("");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const profA = MARKET_PROFESSIONS.find(p => p.id === idA);
  const profB = MARKET_PROFESSIONS.find(p => p.id === idB);
  const canCompare = idA && idB && idA !== idB;

  async function runComparison() {
    setPhase("loading");
    setErrorMsg(null);
    try {
      // Strip weekRoutine to save tokens
      const strip = (p) => {
        const { weekRoutine, ...rest } = p;
        return rest;
      };
      const res = await db.functions.invoke("careerComparison", {
        professionA: strip(profA),
        professionB: strip(profB),
      });
      if (res.data?.error) throw new Error(res.data.error);
      const data = res.data?.comparison;
      if (!data) throw new Error("Resposta inválida da IA — tente novamente.");
      setResult(data);
      setPhase("result");
    } catch (err) {
      console.error("Erro na comparação:", err);
      setErrorMsg(err?.message || "Erro ao gerar comparação. Tente novamente.");
      setPhase("select");
    }
  }

  function handleNewComparison() {
    setIdA("");
    setIdB("");
    setResult(null);
    setErrorMsg(null);
    setPhase("select");
  }

  if (phase === "loading") {
    return <ComparisonLoading />;
  }

  if (phase === "result" && result && profA && profB) {
    return (
      <ComparisonResult
        result={result}
        profA={profA}
        profB={profB}
        onNewComparison={handleNewComparison}
        onBack={onBack}
      />
    );
  }

  // Phase: select
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      className="space-y-6"
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-medium"
        style={{ color: "#646C6F" }}
      >
        <ArrowLeft className="w-4 h-4" /> Voltar ao mapa
      </button>

      {/* Title */}
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Comparar Carreiras
        </h2>
        <p className="text-sm mt-1" style={{ color: "#646C6F" }}>
          Selecione duas profissões para ver uma análise comparativa lado a lado
        </p>
      </div>

      {/* Dropdowns */}
      <div className="rounded-2xl bg-white p-5" style={{ border: "1px solid #E5E9EA" }}>
        <div className="flex flex-col sm:flex-row items-stretch gap-3">
          <ProfessionDropdown
            value={idA}
            onChange={setIdA}
            disabledId={idB}
            label="Profissão A"
          />
          <div className="flex items-end justify-center pb-1">
            <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "#F2F4F4", color: "#646C6F" }}>
              vs
            </span>
          </div>
          <ProfessionDropdown
            value={idB}
            onChange={setIdB}
            disabledId={idA}
            label="Profissão B"
          />
        </div>
      </div>

      {/* Error */}
      {errorMsg && (
        <div className="flex gap-3 p-4 rounded-2xl" style={{ background: "#FADBDF" }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#892E38" }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: "#892E38" }}>{errorMsg}</p>
          </div>
        </div>
      )}

      {/* Compare button */}
      <button
        onClick={runComparison}
        disabled={!canCompare}
        className="w-full py-3.5 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all"
        style={{
          background: canCompare ? "#00DCA4" : "#E5E9EA",
          color: canCompare ? "#003326" : "#99A6AB",
          cursor: canCompare ? "pointer" : "not-allowed",
        }}
      >
        <Sparkles className="w-4 h-4" /> Comparar Carreiras
      </button>
    </motion.div>
  );
}
